'use client';

import { LockClosedIcon, UserIcon } from '@heroicons/react/24/solid';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Bounce, toast } from 'react-toastify';
import { z } from 'zod';
import { signInAction } from '@/actions/sign-in.action';
import { Button } from '@/components/button';
import { Input } from '@/components/Input/Input';
import { useAuthDialog } from '@/context/AuthDialogProvider';

// Zod validation schema
const loginSchema = z.object({
  email: z
    .email('Invalid email format')
    .min(1, 'Email is required')
    .max(50, 'Email is too long'),
  password: z
    .string()
    .min(1, 'Password is required')
    .max(50, 'Password is too long'),
});

type SignInFormData = z.infer<typeof loginSchema>;

export const SignInForm = () => {
  const { toggleIsOpen, changeMode } = useAuthDialog();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignInFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: SignInFormData) => {
    try {
      await signInAction(data);
      toast.info('Login successful', {
        position: 'top-right',
        autoClose: Number.POSITIVE_INFINITY,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'dark',
        transition: Bounce,
      });
      toggleIsOpen();
    } catch (_error) {
      toast.error('Invalid email or password');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-2">
        <div className="mb-4 flex w-full max-w-[480px] flex-col gap-5 self-center">
          <h2 className="text-balance font-semibold text-2xl">
            Welcome back, please sign in
          </h2>
        </div>

        <div className="flex w-full max-w-[480px] flex-col gap-5 self-center">
          <div className="flex w-full flex-col gap-2">
            <Input
              {...register('email')}
              error={errors.email?.message}
              label="Email"
              leftIcon={<UserIcon className="size-5" />}
              placeholder="eg. john@example.com"
              type="email"
            />

            <Input
              {...register('password')}
              error={errors.password?.message}
              label="Password"
              leftIcon={<LockClosedIcon className="size-5" />}
              placeholder="your password"
              type="password"
            />

            <div className="flex justify-end">
              <button
                className="cursor-pointer text-light-blue text-sm hover:underline"
                onClick={() => changeMode('reset-password')}
                type="button"
              >
                Forgot password?
              </button>
            </div>
          </div>

          <Button
            disabled={isSubmitting}
            onClick={(e) => e.stopPropagation()}
            size="lg"
            type="submit"
            variant="primary"
          >
            Sign In
          </Button>
        </div>
      </div>
    </form>
  );
};
